import { compose } from 'recompose';
import { connect } from 'react-redux';
import { get, omit } from 'lodash';

import { ErrorActions } from '@zapier/toolbox-redux-framework';
import type { Dateable } from '@zapier/date';

import * as Account from 'app/entities/Account';
import * as Member from 'app/entities/Member';
import * as Profile from 'app/entities/Profile';
import AuthActions from 'app/auth/AuthActions';
import LoadingBlockOrComponent from 'app/common/components/LoadingBlockOrComponent';
import LoadingBlock from 'app/common/components/LoadingBlock';
import hocify from 'app/common/components/hocify';
import { AuthTypes } from 'app/auth/utils/constants';
import {
  computeAuthType,
  getAuthState,
  getAuthsBySelectedApi,
  isAuthRestricted,
  isAuthStaleAfterPolling,
} from 'app/auth/Selectors';

import AuthAccount from './AuthAccount';

import type { AuthType } from 'app/auth/types';
import type { Service } from 'app/service/types';

import type { FlowNode } from './types';

type Props = {
  auths: Array<AuthType>;
  node: FlowNode;
  service: Service;
};

type AuthProp = {
  selected_api: string;
  label: string;
  owner_id: number;
  id: number;
  is_shared: boolean;
  title: string;
  date: Dateable;
  authStates?: Object;
};

type State = {
  auth: AuthProp;
};

export const mapStateToProps = (state: State, props: Props) => {
  const { node } = props;

  const isTeamAccount = Account.selectors.isTeam(
    Account.selectors.all.currentAccount(state)
  );
  const currentProfileId = Profile.selectors.all.currentProfileId(state);

  return {
    isLoading:
      isTeamAccount &&
      !!node?.authentication_id &&
      Member.selectors.all.isLoadImpending(state),
    LoadingComponent: LoadingBlock,
    auths: [
      isTeamAccount &&
      node?.authentication_id &&
      isAuthRestricted(node.authentication_id, state)
        ? {
            api: '',
            canEdit: false,
            canShare: false,
            connectionLabel: '',
            id: node.authentication_id,
            isShared: false,
            isSharable: false,
            isRestricted: true,
            numZaps: null,
            state: {},
            title: `${
              Member.selectors.normalizedName(
                Member.selectors.all.entity(
                  node?.authentication_owner_id,
                  state
                ),
                state
              ) || 'Team Member'
            }'s Account`,
            type: AuthTypes.RESTRICTED,
            addedDate: '',
          }
        : null,
      ...getAuthsBySelectedApi(node.selected_api, state).map(auth => ({
        api: auth.selected_api,
        canEdit: currentProfileId === auth.owner_id,
        canShare: isTeamAccount && currentProfileId === auth.owner_id,
        connectionLabel: auth.label,
        id: auth.id,
        isShared: auth.is_shared,
        isSharable: isTeamAccount,
        isRestricted: false,
        numZaps: get(getAuthState(auth.id, state), 'numZaps'),
        state: omit(getAuthState(auth.id, state), ['id', 'numZaps']),
        title: auth.title || 'My Untitled Account',
        type: computeAuthType(auth),
        addedDate: auth.date,
        isStale: isAuthStaleAfterPolling(auth, getAuthState(auth.id, state)),
      })),
    ].filter(Boolean),
    authStates: state.auth.authStates,
    canHaveRestrictedAuth: isTeamAccount,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  onLoad: () => {
    dispatch(AuthActions.loadAuthsIfNotLoaded());
    dispatch(AuthActions.loadAuthCounts());
    dispatch(Member.actions.loadCollectionIfTeamAccount());
  },
  connectAuth: (...args) => {
    dispatch(AuthActions.connectAuth(...args));
  },
  reconnectAuth: async (...args) => {
    await dispatch(AuthActions.reconnectAuth(...args));
    await ownProps.onReconnectedAuth();
  },
  updateAuth: (...args) => {
    dispatch(AuthActions.updateAuth(...args));
  },
  updateAuthState: (...args) => {
    dispatch(AuthActions.updateAuthState(...args));
  },
  testAuth: (...args) => {
    dispatch(AuthActions.testAuth(...args));
  },
  saveAuth: (...args) => {
    dispatch(AuthActions.saveAuth(...args));
  },
  resetAuthStates: (...args) => {
    dispatch(AuthActions.resetAuthStates(...args));
  },
  clearErrors: () => {
    dispatch(ErrorActions.clearErrors());
  },
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  hocify(LoadingBlockOrComponent)
)(AuthAccount);
